/**
 * OWLY CRM - Testing Utilities
 * Utilities for generating test data and performance testing
 */

import { Lead } from '../../types';
import { mockLeads } from '../mock/leads';

interface TestingConfig {
  enabled: boolean;
  leadMultiplier: number;
  totalLeads: number;
  performanceMode: boolean;
  debugMode?: boolean;
  showTestBadges?: boolean;
}

/**
 * Genera leads optimizados para testing basado en la configuración
 * Usa mockLeads con datos reales de proyectos inmobiliarios
 * Genera exactamente config.totalLeads cantidad de leads
 */
export const generateTestLeads = (config: TestingConfig): Lead[] => {
  if (!config.enabled) {
    return mockLeads; // Retorna los leads mock con datos reales
  }

  const baseLeads = mockLeads; // 6 leads base con datos reales
  const desiredTotal = config.totalLeads;

  // Si se pide menos o igual que los mockLeads disponibles, retornar slice
  if (desiredTotal <= baseLeads.length) {
    return baseLeads.slice(0, desiredTotal);
  }

  // Calcular cuántas repeticiones necesitamos
  const repetitions = Math.ceil(desiredTotal / baseLeads.length);
  const generatedLeads: Lead[] = [];

  for (let batch = 0; batch < repetitions; batch++) {
    const batchLeads = baseLeads.map((lead, index) => {
      // Calcular el índice global para este lead
      const globalIndex = batch * baseLeads.length + index;

      // Si ya alcanzamos el total deseado, no agregar más
      if (globalIndex >= desiredTotal) {
        return null;
      }

      return {
        ...lead,
        id: `${lead.id}-batch${batch}-${index}`,
        name: lead.name, // Mantener nombre original sin modificaciones
        // Variar ligeramente el valor para cada batch
        value: lead.value + (batch * 10000) + (Math.random() * 50000),
        // Variar scoring MANTENIENDO múltiplos de 5
        scoring: lead.scoring ? {
          ...lead.scoring,
          // Mantener el total sin variación para preservar múltiplos de 5
          total: lead.scoring.total
        } : undefined,
        // Variar datos de perfil en modo no-performance
        perfil: config.performanceMode ? lead.perfil : {
          ...lead.perfil,
          telefono: lead.perfil?.telefono ?
            lead.perfil.telefono.replace(/\d{3}$/, (Math.floor(Math.random() * 1000)).toString().padStart(3, '0')) :
            undefined,
          email: lead.perfil?.email ?
            lead.perfil.email.replace(/(\w+)@/, `$1${batch}@`) :
            undefined
        }
      };
    }).filter(lead => lead !== null);

    generatedLeads.push(...batchLeads);
  }

  // Asegurar que retornamos exactamente la cantidad solicitada
  return generatedLeads.slice(0, desiredTotal);
};

/**
 * Calcula métricas de rendimiento estimadas
 */
export const calculatePerformanceMetrics = (config: TestingConfig) => {
  const totalLeads = config.totalLeads * config.leadMultiplier;
  const estimatedEvents = totalLeads * 2; // ~2 eventos por lead
  const estimatedMemoryKB = Math.round(totalLeads * 0.5); // ~0.5KB por lead
  const renderingComplexity = totalLeads > 1000 ? 'Alta' : totalLeads > 500 ? 'Media' : 'Baja';

  return {
    totalLeads,
    estimatedEvents,
    estimatedMemoryKB,
    renderingComplexity,
    recommendPerformanceMode: totalLeads > 800
  };
};

/**
 * Hook para logging de debug cuando está habilitado
 */
export const debugLog = (config: TestingConfig, message: string, data?: any) => {
  if (config.enabled && config.debugMode && process.env.NODE_ENV === 'development') {
    if (data) {
    }
  }
};

/**
 * Utilidad para mostrar badges de testing en componentes
 */
export const getTestBadge = (config: TestingConfig) => {
  if (!config.enabled || !config.showTestBadges) return null;

  return {
    text: `TEST x${config.leadMultiplier}`,
    className: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 text-xs px-2 py-1 rounded-md font-medium',
    shouldShow: true
  };
};
